import { NavLink, Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useSession } from '../auth/session';
import { NOM_PRODUIT } from '../produit';
import { libelleRole } from './roles';

/**
 * En-tête et pied de page repris du design « Radars Home » : même barre
 * collante, même pied sombre. Le radar public et cet outil doivent se
 * ressembler (§5.1).
 */

type Entree = { vers: string; libelle: string; role?: 'encadrement' | 'admin' };

const NAVIGATION: Entree[] = [
  { vers: '/', libelle: 'Radars' },
  { vers: '/ma-fiche', libelle: 'My profile' },
  { vers: '/dashboards', libelle: 'Dashboards', role: 'encadrement' },
  { vers: '/ecart-radar', libelle: 'Radar gap', role: 'encadrement' },
  { vers: '/qui-connait', libelle: 'Who knows' },
  { vers: '/suivi', libelle: 'Tracking', role: 'encadrement' },
  { vers: '/administration', libelle: 'Admin', role: 'admin' },
];

function Entete(): JSX.Element {
  const session = useSession();

  return (
    <header className="entete">
      <div className="contenu entete__barre">
        <Link to="/" className="entete__marque">
          <img src="/assets/elyadata-logo.svg" alt="Elyadata" className="entete__logo" />
          <span className="entete__filet" aria-hidden="true" />
          <span className="entete__nom">{NOM_PRODUIT}</span>
        </Link>

        <nav className="entete__nav" aria-label="Main navigation">
          {NAVIGATION.filter((e) => !e.role || session.estAuMoins(e.role)).map((e) => (
            <NavLink
              key={e.vers}
              to={e.vers}
              end={e.vers === '/'}
              className={({ isActive }) => `entete__lien${isActive ? ' entete__lien--actif' : ''}`}
            >
              {e.libelle}
            </NavLink>
          ))}
          {session.moi ? (
            <button
              type="button"
              className="bouton bouton--fantome bouton--petit"
              onClick={() => void session.deconnecte()}
              title={`${session.moi.personne.email} — ${libelleRole(session.moi.personne.role)}`}
            >
              {session.moi.personne.nom.split(' ')[0]} · sign out
            </button>
          ) : null}
        </nav>
      </div>
    </header>
  );
}

function PiedDePage({ trimestre }: { trimestre?: string | null }): JSX.Element {
  return (
    <footer className="pied">
      <div className="contenu pied__grille">
        <div style={{ maxWidth: 300 }}>
          <img src="/assets/elyadata-logo-white.svg" alt="Elyadata" className="pied__logo" />
          <p className="pied__texte">
            Quarterly capability mapping. Internal tool, for Elyadata staff only. La Marsa,
            Tunis.
          </p>
        </div>

        <div className="pied__colonne">
          <span className="pied__titre">My campaign</span>
          <Link to="/ma-fiche">Fill in my profile</Link>
          <Link to="/qui-connait">Who knows a technology</Link>
          <Link to="/">The radars</Link>
        </div>

        <div className="pied__colonne">
          <span className="pied__titre">The radar</span>
          <a href="https://radar.elyadata.com/">Technology Radar</a>
          <a href="https://www.elyadata.com/">elyadata.com</a>
          <a href="https://github.com/elyadata">Open source</a>
        </div>

        <div className="pied__colonne">
          <span className="pied__titre">How to read it</span>
          <p className="pied__texte">
            A level speaks about the person’s skill, never about the technology itself. Every level
            from 3 up requires written evidence.
          </p>
        </div>

        {/* Mêmes bureaux que sur elyadata.com — un repère familier plutôt qu'une
            adresse recopiée : deux villes, pas de lien, comme sur le site public. */}
        <div className="pied__colonne">
          <span className="pied__titre">Offices</span>
          <p className="pied__texte">
            <strong>Paris</strong>
            <br />
            128 rue La Boétie, 75008
          </p>
          <p className="pied__texte">
            <strong>Tunis</strong>
            <br />
            Rue de l’île de Failaka, Les berges du lac 2
          </p>
        </div>
      </div>

      <div className="contenu">
        <div className="pied__bas">
          <span>© {new Date().getFullYear()} Elyadata. All rights reserved. Named data, internal use only.</span>
          <span className="mono">{trimestre ? `${trimestre} · campaign open` : 'no campaign open'}</span>
        </div>
      </div>
    </footer>
  );
}

export function Coquille({ children }: { children: ReactNode }): JSX.Element {
  const session = useSession();

  return (
    <div className="page">
      <a href="#contenu-principal" className="saut-contenu">
        Skip to content
      </a>
      <Entete />
      <main id="contenu-principal" className="page__corps">
        {children}
      </main>
      <PiedDePage trimestre={session.moi?.campagneOuverte?.trimestre ?? null} />
    </div>
  );
}
